/**
 * hooks/useFormTemplateLoader.ts
 *
 * Handles loading an existing form template (draft or active) from Supabase,
 * reconstructing NeedItem[] from template_fields only (all types including 'document').
 * Extracted from CreateFormTemplate to keep the page component under 300 lines.
 *
 * Updated Jun 2026: simplified - all field types (including 'document') now live in
 * template_fields. The form_template_documents table no longer exists. Catalog lookups
 * removed; the field label IS the source of truth.
 * Updated Jun 2026: reads short_label from template_fields for admin display.
 */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { NeedItem } from '../types/needs';
import { safeParseOptions, parseConditionsFromDb } from '../utils/jsonHelpers';
import { topologicalSortNeeds } from '../utils/conditionOrderUtils';
import { captureError } from '../utils/errorReporting';

interface UseFormTemplateLoaderParams {
  templateId: string | undefined;
  userId: string | undefined;
  userProfileLoaded: boolean;
  isUserAdmin: boolean;
  navigate: (path: string) => void;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
  onLoadComplete: () => void;
}

export function useFormTemplateLoader({
  templateId,
  userId,
  userProfileLoaded,
  isUserAdmin,
  navigate,
  showToast,
  onLoadComplete,
}: UseFormTemplateLoaderParams) {
  const [loadingTemplate, setLoadingTemplate] = useState(!!templateId);
  const [name, setName] = useState('');
  const [needs, setNeeds] = useState<NeedItem[]>([]);
  const [isEditingActive, setIsEditingActive] = useState(false);

  const hasLoadedTemplate = useRef(false);
  const onLoadCompleteRef = useRef(onLoadComplete);
  onLoadCompleteRef.current = onLoadComplete;

  useEffect(() => {
    if (!templateId || !userId || !userProfileLoaded) return;
    if (hasLoadedTemplate.current) return;

    const loadTemplate = async () => {
      try {
        const { data: template, error: templateError } = isUserAdmin
          ? await supabase
              .from('form_templates')
              .select('*')
              .eq('id', templateId)
              .maybeSingle()
          : await supabase
              .from('form_templates')
              .select('*')
              .eq('id', templateId)
              .eq('user_id', userId)
              .maybeSingle();

        if (templateError) throw templateError;
        if (!template) {
          showToast('Formulaire introuvable', 'error');
          navigate('/my-forms');
          return;
        }

        setIsEditingActive(template.status === 'active');

        const { data: fields, error: fieldsError } = await supabase
          .from('template_fields')
          .select('*')
          .eq('template_id', templateId)
          .order('position');
        if (fieldsError) throw fieldsError;

        const reconstructedNeeds: NeedItem[] = (fields || []).map((field) => ({
          id: field.id,
          type: field.type,
          label: field.label,
          short_label: field.short_label ?? null,
          description: field.description,
          is_required: field.is_required,
          position: field.position,
          options: field.options ? safeParseOptions(field.options) : undefined,
          conditions: parseConditionsFromDb(
            field.conditions,
            null,
            null
          ),
          condition_operator: (field.condition_operator as 'or' | 'and') || 'or',
          section: field.section,
          is_system: field.is_system ?? false,
        }));

        reconstructedNeeds.sort((a, b) => a.position - b.position);

        const sortedNeeds = topologicalSortNeeds(reconstructedNeeds);

        hasLoadedTemplate.current = true;
        setName(template.name);
        setNeeds(sortedNeeds);
        onLoadCompleteRef.current();
      } catch (error) {
        console.error('Error loading template:', error);
        captureError(error, { hook: 'useFormTemplateLoader', action: 'loadTemplate', templateId });
        showToast('Erreur lors du chargement du formulaire', 'error');
        navigate('/my-forms');
      } finally {
        setLoadingTemplate(false);
      }
    };

    loadTemplate();
  }, [templateId, userId, userProfileLoaded, isUserAdmin, navigate, showToast]);

  return {
    loadingTemplate,
    name,
    setName,
    needs,
    setNeeds,
    isEditingActive,
  };
}
